// Share an artifact by link, or take the link back (Adam, 13 Sep 2026).
// Networking lives here; ShareArtifactDialog only renders what it is given.
import { useCallback, useState } from 'react'
import axios from 'axios'
import { API_BASE_URL } from '../constants/system'

export interface ArtifactShare {
  share_id: string
  url: string
  /** Signed-in viewers may discuss the page with Douglas (Adam, 16 Sep); off by default, it spends tokens. */
  discuss_enabled: boolean
}

export type ArtifactShareState =
  | { status: 'idle' }
  | { status: 'sharing' }
  | { status: 'shared'; share: ArtifactShare }
  | { status: 'revoking'; share: ArtifactShare }
  | { status: 'error'; message: string }

export default function useArtifactShare() {
  const [state, setState] = useState<ArtifactShareState>({ status: 'idle' })

  const handleShare = useCallback(async (artifactId: string) => {
    setState({ status: 'sharing' })
    try {
      const res = await axios.post<ArtifactShare>(`${API_BASE_URL}/agent/artifacts/${encodeURIComponent(artifactId)}/share`)
      setState({ status: 'shared', share: res.data })
      return res.data
    } catch (err) {
      setState({ status: 'error', message: err instanceof Error ? err.message : 'Could not share' })
      return null
    }
  }, [])

  const handleRevoke = useCallback(async (artifactId: string) => {
    setState((s) => (s.status === 'shared' ? { status: 'revoking', share: s.share } : s))
    try {
      await axios.delete(`${API_BASE_URL}/agent/artifacts/${encodeURIComponent(artifactId)}/share`)
      setState({ status: 'idle' })
      return true
    } catch (err) {
      setState({ status: 'error', message: err instanceof Error ? err.message : 'Could not stop sharing' })
      return false
    }
  }, [])

  const handleSetDiscuss = useCallback(async (artifactId: string, enabled: boolean) => {
    try {
      const res = await axios.patch<ArtifactShare>(`${API_BASE_URL}/agent/artifacts/${encodeURIComponent(artifactId)}/share`, { discuss_enabled: enabled })
      setState((s) => (s.status === 'shared' ? { status: 'shared', share: res.data } : s))
      return true
    } catch (err) {
      setState({ status: 'error', message: err instanceof Error ? err.message : 'Could not change the setting' })
      return false
    }
  }, [])

  const reset = useCallback(() => setState({ status: 'idle' }), [])

  return { state, handleShare, handleRevoke, handleSetDiscuss, reset }
}
